import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, TextField, Grid, CircularProgress } from '@mui/material';
import { createPortal } from 'react-dom';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import SearchIcon from '@mui/icons-material/Search';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { AlertMessagesContext } from 'react-alert-messages';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { saveAs } from 'file-saver';
import CsvHelper from 'helpers/CsvHelper';
import SyncIcon from '@mui/icons-material/Sync';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import PurchaseSummaryReportServices from 'services/PurchaseSummaryReportServices';
import PrintIcon from '@mui/icons-material/Print';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';

function VendorPurchaseSummary() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [filterFromDate, setFilterFromDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());

  const [searchText, setSearchText] = useState('');
  const [vendorData, setVendorData] = useState('');
  const [filteredVendorData, setFilteredVendorData] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    id: tCommon('ID'),
    name: tCommon('Vendor'),
    total: {
      title: tCommon('Total Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.total || 0);
      },
    },
  };

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(filteredVendorData, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `VENDOR-PURCHASE-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const headerData = [
    {
      label: tCommon('ID'),
      id: 'id',
      type: 'text',
    },
    {
      label: tCommon('Vendor'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Total Amount'),
      id: 'total',
      type: 'floatAmount',
    },
  ];

  const getPurchase = useCallback(async () => {
    setLoadingIndicator(true);
    try {
      const { startDate, endDate } = dynamicDateRange('VENDOR_PURCHASE_SUMMARY');
      setFilterFromDate(startDate);
      setFilterToDate(endDate);

      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(startDate),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(endDate),
      };

      const res = await PurchaseSummaryReportServices.getVendorPurchaseSummary(data);
      setVendorData(res);
      setFilteredVendorData(res);
      setPrevFilterData({
        filterFromDate: startDate,
        filterToDate: endDate,
      });
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    } finally {
      setLoadingIndicator(false);
    }
  }, [postAlertMessage]);

  const generatePurchaseHistory = () => {
    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate,
      filterToDate,
    });
    getPurchase();
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate.getTime() !== prevFilterData.filterFromDate.getTime() ||
      filterToDate.getTime() !== prevFilterData.filterToDate.getTime()
    );
  }, [filterFromDate, filterToDate, prevFilterData]);

  useEffect(() => {
    generatePurchaseHistory();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Vendor Purchase Summary Report'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredVendorData,
      {
        [tCommon('Total Amount')]: summary.totalAmount,
      }
    );
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setFilteredVendorData((vendorData || []).filter((data) => data.name.toLowerCase().includes(value)));
  };

  const _getVendorPurchasesReportSummart = (vendorData) => {
    let totalAmount = 0;
    (vendorData || []).forEach((data) => {
      totalAmount += data?.total;
    });
    return {
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
    };
  };

  const summary = _getVendorPurchasesReportSummart(filteredVendorData);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a', marginRight: 5 }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            Csv
          </Button>
        </div>
      </div>
      <div className={styles.changeable}>
        <Grid container spacing={1} alignItems="flex-end">
          <Grid item xs={12}>
            <h2 className={styles.subTitle}>
              {tReport('Vendor Purchase Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </Grid>

          <Grid item xs={12} md={3}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="VENDOR_PURCHASE_SUMMARY"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={tCommon('Search purchase')}
              value={searchText}
              onChange={(e) => {
                handleSearch(e.target.value);
              }}
              InputProps={{
                endAdornment: <SearchIcon />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              className={styles.greenBtn}
              onClick={generatePurchaseHistory}
              disabled={isShowGenerateButton === false || loadingIndicator}
              startIcon={loadingIndicator ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            >
              {tCommon('Load Purchases')}
            </Button>
          </Grid>
        </Grid>
      </div>
      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : (
        vendorData &&
        (filteredVendorData && filteredVendorData.length ? (
          <div className={styles.tableContainer}>
            <Grayout open={isShowGenerateButton} />
            <DataTable
              columns={headerData}
              rows={filteredVendorData}
              summary={{ name: tCommon('Total'), total: summary.totalAmount }}
            />
          </div>
        ) : (
          !loadingIndicator && (
            <Info
              title={tReport('You have no purchases to list')}
              content={tReport("You haven't any purchase to list with given properties")}
            />
          )
        ))
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(VendorPurchaseSummary);
